/**
 * AMClientDetail — Client 360° detail page
 *
 * Shows full client information in a tabbed interface:
 * Overview, Communications, Leads, Contracts, Notes.
 */

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AMLayout } from "@/components/am/AMLayout";
import { useAccountManager } from "@/hooks/useAccountManager";
import { useAMClientDetail } from "@/hooks/useAMClientDetail";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  MessageSquare,
  LayoutDashboard,
  Mail,
  Phone,
  Package,
  FileText,
  StickyNote,
  Loader2,
  AlertCircle,
} from "lucide-react";
import ClientOverviewTab from "@/components/am/ClientOverviewTab";
import ClientChat from "@/components/am/ClientChat";
import ClientLeadsTab from "@/components/am/ClientLeadsTab";
import ClientContractsTab from "@/components/am/ClientContractsTab";
import ClientNotesTab from "@/components/am/ClientNotesTab";
import { useHealthScore } from "@/hooks/useHealthScore";
import { useAMTasks } from "@/hooks/useAMTasks";
import ClientHealthBadge from "@/components/am/ClientHealthBadge";
import type { AMConversation } from "@/hooks/useAMMessages";

const tierColors: Record<string, string> = {
  Elite: "bg-purple-100 text-purple-700 border-purple-200",
  Gold: "bg-amber-100 text-amber-700 border-amber-200",
  Silver: "bg-slate-100 text-slate-700 border-slate-200",
  Bronze: "bg-orange-100 text-orange-700 border-orange-200",
};

const verticalLabels: Record<string, string> = {
  life_insurance: "Life Insurance",
  mortgage: "Mortgage",
};

export default function AMClientDetail() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { accountManager, loading: amLoading } = useAccountManager();
  const {
    agent,
    contracts,
    leads,
    notes,
    recentMessages,
    loading,
    error,
    addNote,
    updateNote,
    deleteNote,
  } = useAMClientDetail(agentId, accountManager?.id);

  const healthScore = useHealthScore(agent, contracts, leads, agentId);
  const { tasks } = useAMTasks(accountManager?.id || null);
  const clientOpenTasks = tasks
    .filter((t) => t.agentId === agentId && t.status !== "done")
    .map((t) => ({ id: t.id, title: t.title, priority: t.priority, dueDate: t.dueDate, status: t.status }));

  const [userId, setUserId] = useState("");

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) setUserId(data.user.id);
    });
  }, []);

  // Build a conversation object for the chat component
  const conversation: AMConversation | null =
    agent && accountManager
      ? {
          agent_id: agent.id,
          am_id: accountManager.id,
          agent_name: agent.name,
          agent_company: agent.company,
          last_message: "",
          last_message_at: "",
          unread_count: 0,
        }
      : null;

  if (amLoading || loading) {
    return (
      <AMLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mr-2" />
          <span className="text-muted-foreground">Loading client details...</span>
        </div>
      </AMLayout>
    );
  }

  if (error || !agent) {
    return (
      <AMLayout>
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <AlertCircle className="h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">{error || "Client not found."}</p>
          <Button variant="outline" size="sm" onClick={() => navigate("/am")}>
            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
            Back to My Clients
          </Button>
        </div>
      </AMLayout>
    );
  }

  return (
    <AMLayout>
      {/* Header */}
      <div className="mb-6">
        {/* Back link */}
        <Button
          variant="ghost"
          size="sm"
          className="mb-4 -ml-2 text-muted-foreground hover:text-foreground"
          onClick={() => navigate("/am")}
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
          My Clients
        </Button>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              {/* Avatar */}
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center text-foreground font-bold text-lg flex-shrink-0">
                {agent.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2)}
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">{agent.name}</h1>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {agent.company && (
                    <span className="text-sm text-muted-foreground">{agent.company}</span>
                  )}
                  <Badge variant="outline" className="text-[10px]">
                    {verticalLabels[agent.vertical] || agent.vertical}
                  </Badge>
                  <Badge variant="outline" className={`text-[10px] ${tierColors[agent.tier] || ""}`}>
                    {agent.tier}
                  </Badge>
                  <Badge variant={agent.is_active ? "default" : "secondary"} className="text-[10px]">
                    {agent.is_active ? "Active" : "Inactive"}
                  </Badge>
                  {!healthScore.loading && (
                    <ClientHealthBadge
                      score={healthScore.overall}
                      status={healthScore.status}
                      delta7d={healthScore.trend7d}
                      size="sm"
                    />
                  )}
                </div>
                {(agent.email || agent.phone) && (
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    {agent.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {agent.email}
                      </span>
                    )}
                    {agent.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {agent.phone}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate("/am/inbox")}
            >
              <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
              Message
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="w-full justify-start bg-muted/50 p-1 rounded-lg">
          <TabsTrigger value="overview" className="text-xs gap-1.5">
            <LayoutDashboard className="h-3.5 w-3.5" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="communications" className="text-xs gap-1.5">
            <MessageSquare className="h-3.5 w-3.5" />
            Communications
          </TabsTrigger>
          <TabsTrigger value="leads" className="text-xs gap-1.5">
            <Package className="h-3.5 w-3.5" />
            Leads
          </TabsTrigger>
          <TabsTrigger value="contracts" className="text-xs gap-1.5">
            <FileText className="h-3.5 w-3.5" />
            Contracts
          </TabsTrigger>
          <TabsTrigger value="notes" className="text-xs gap-1.5">
            <StickyNote className="h-3.5 w-3.5" />
            Notes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <ClientOverviewTab
            agent={agent}
            contracts={contracts}
            leads={leads}
            recentMessages={recentMessages}
            openTasks={clientOpenTasks}
            healthScore={healthScore}
          />
        </TabsContent>

        <TabsContent value="communications">
          {conversation && accountManager && userId ? (
            <div className="bg-card rounded-2xl border border-border" style={{ height: "calc(100vh - 22rem)" }}>
              <ClientChat
                conversation={conversation}
                amId={accountManager.id}
                senderId={userId}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              Unable to load chat.
            </div>
          )}
        </TabsContent>

        <TabsContent value="leads">
          <ClientLeadsTab leads={leads} contracts={contracts} />
        </TabsContent>

        <TabsContent value="contracts">
          <ClientContractsTab contracts={contracts} />
        </TabsContent>

        <TabsContent value="notes">
          <ClientNotesTab
            notes={notes}
            onAdd={addNote}
            onUpdate={updateNote}
            onDelete={deleteNote}
          />
        </TabsContent>
      </Tabs>
    </AMLayout>
  );
}
